import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { fillPath, isFreeTemplateCode, useCompanies, useCreateReport, useStartFreeReport, useTemplateVersion, useTemplates } from '../api/hooks'
import type { CompanyDto, HeaderField, TemplateSummary } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { problemOf } from '../api/client'
import DateInput from '../components/DateInput'
import { Modal } from '../components/ui'
import StartFreeReportPage, { useFreeStartError } from './StartFreeReportPage'

type HeaderValue = string | string[]

const todayIso = () => new Date().toISOString().slice(0, 10)
const nowHm = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` }

/** /reports/new?free=1 starts a free-form report right away; otherwise the normal template form is shown. */
export default function NewReportPage() {
  const [params] = useSearchParams()
  return params.get('free') === '1' ? <StartFreeReportPage /> : <NewReportForm />
}

/**
 * Pick a form card → a small "Rapora başla" sheet with everything filled in (company, start date-time, auditor, a default
 * title) → straight into the report. Other header fields are optional here (collapsed) and can be filled later.
 */
function NewReportForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const templates = useTemplates()
  const publishedTemplates = (templates.data ?? []).filter((x) => x.currentVersionId)
  const companies = useCompanies()
  const [template, setTemplate] = useState<TemplateSummary | null>(null)
  const startFree = useStartFreeReport()
  const startFreeError = useFreeStartError(startFree.error)
  // "Serbest Saha Turu": no form to fill in first — the card asks, then creates the draft and opens the item editor.
  const goFree = () => { if (confirm(`${t('freeReport.startConfirmTitle')}\n\n${t('freeReport.startConfirmText')}`)) startFree.mutate(undefined, { onSuccess: (created) => navigate(`/reports/${created.id}/free`) }) }

  // The company a report is opened for: the user's own (single scoped company), else the tenant's own, else the only one.
  const defaultCompany = useMemo(() => {
    const list = companies.data ?? []
    const scopedIds = new Set((user?.roles ?? []).map((r) => r.companyId).filter((id): id is string => !!id))
    const scoped = scopedIds.size === 1 ? list.find((c) => scopedIds.has(c.id)) : undefined
    return scoped ?? list.find((c) => c.type === 'Self') ?? (list.length === 1 ? list[0] : undefined) ?? list[0]
  }, [companies.data, user?.roles])

  return (
    <div className="stack" style={{ maxWidth: 760 }}>
      <h2>{t('nav.newReport')}</h2>
      <button type="button" className="free-start-card" onClick={goFree} disabled={startFree.isPending}>
        <span className="free-start-icon" aria-hidden="true">📷</span>
        <span className="free-start-text">
          <b>{t('freeReport.cardTitle')}</b>
          <span className="small">{t('freeReport.cardHint')}</span>
        </span>
        <span className="free-start-go">{startFree.isPending ? t('freeReport.starting') : t('freeReport.cardAction')}</span>
      </button>
      {startFree.isError ? <p className="error" style={{ margin: 0 }}>{startFreeError}</p> : null}
      <div className="card stack">
        <div className="small" style={{ marginBottom: 6 }}>{t('reports.pickTemplate')}</div>
        <div className="template-cards">
          {publishedTemplates.map((x) => (
            <button key={x.id} type="button" className={`template-card ${template?.id === x.id ? 'selected' : ''}`} onClick={() => (isFreeTemplateCode(x.code) ? goFree() : setTemplate(x))} disabled={isFreeTemplateCode(x.code) && startFree.isPending} aria-pressed={template?.id === x.id}>
              <span className="badge">{x.code}</span>
              <b>{x.name}</b>
              <span className="muted small">{x.questionCount} {t('builder.q')} · {t(`formKind.${x.kind}`)}</span>
            </button>
          ))}
          {templates.data && publishedTemplates.length === 0 ? <p className="muted small">{t('templates.empty')}</p> : null}
        </div>
        <p className="muted small" style={{ marginBottom: 0 }}>{t('reports.templateCardHint')}</p>
        {companies.data && companies.data.length === 0 ? <p className="error small" style={{ margin: 0 }}>{t('reports.noCompany')}</p> : null}
      </div>
      {template ? (
        <StartReportModal template={template} companies={companies.data ?? []} defaultCompany={defaultCompany} onClose={() => setTemplate(null)} />
      ) : null}
    </div>
  )
}

function StartReportModal({ template, companies, defaultCompany, onClose }: { template: TemplateSummary; companies: CompanyDto[]; defaultCompany?: CompanyDto; onClose: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const version = useTemplateVersion(template.currentVersionId)
  const create = useCreateReport()
  const [companyId, setCompanyId] = useState(defaultCompany?.id ?? '')
  const [siteId, setSiteId] = useState('')
  const [title, setTitle] = useState('')
  const [date, setDate] = useState(todayIso())
  const [time, setTime] = useState(nowHm())
  const [more, setMore] = useState(false)
  const [values, setValues] = useState<Record<string, HeaderValue>>({})
  useEffect(() => { if (!companyId && defaultCompany) setCompanyId(defaultCompany.id) }, [defaultCompany, companyId])

  const company = companies.find((c) => c.id === companyId)
  const str = (key: string) => { const v = values[key]; return typeof v === 'string' ? v : '' }
  const list = (key: string) => { const v = values[key]; return Array.isArray(v) ? v : [] }
  const setStr = (key: string, v: string) => setValues((prev) => ({ ...prev, [key]: v }))
  const setList = (key: string, v: string[]) => setValues((prev) => ({ ...prev, [key]: v }))

  // Header fields the sheet still shows: required ones inline, the rest under "Diğer üst bilgiler".
  const extraFields = useMemo(
    () => (version.data?.headerFields ?? []).filter((f) => !f.isAuditorList && !f.isInstanceCode && !f.readOnly && !f.isAuditDate && f.type !== 'Company' && f.type !== 'Site').sort((a, b) => a.sortOrder - b.sortOrder),
    [version.data],
  )
  const requiredFields = extraFields.filter((f) => f.required)
  const optionalFields = extraFields.filter((f) => !f.required)
  const dateField = version.data?.headerFields.find((f) => f.isAuditDate)
  const defaultTitle = company ? `${template.name} — ${company.name}` : template.name
  const timeOk = /^([01]\d|2[0-3]):[0-5]\d$/.test(time)

  function toValue(f: HeaderField, raw: HeaderValue | undefined): unknown {
    if (f.type === 'DateList') return (Array.isArray(raw) ? raw : []).filter(Boolean)
    if (f.type === 'DateRange') {
      const [start = '', end = ''] = Array.isArray(raw) ? raw : []
      return start || end ? [start, end] : null
    }
    const v = (typeof raw === 'string' ? raw : '').trim()
    if (f.type === 'Number') return v === '' ? null : Number(v)
    return v
  }
  const missingRequired = requiredFields.filter((f) => { const v = toValue(f, values[f.key]); return v === null || v === '' || (Array.isArray(v) && v.length === 0) })

  async function start() {
    if (!company || !version.data) return
    const header: Record<string, unknown> = {}
    for (const f of version.data.headerFields) {
      if (f.type === 'Company') header[f.key] = company.name
      else if (f.isAuditDate) header[f.key] = f.type === 'DateList' ? [date] : f.type === 'DateRange' ? [date, date] : date
      else if (f.key === 'documentNo') header[f.key] = (version.data.settings as { documentNo?: string }).documentNo ?? template.code
      else if (extraFields.includes(f)) header[f.key] = toValue(f, values[f.key])
    }
    const auditors = user ? [{ userId: user.id, displayName: user.fullName, isLead: true }] : []
    try {
      const created = await create.mutateAsync({ templateId: template.id, companyId: company.id, siteId: siteId || undefined, title: title.trim() || undefined, headerValues: header, auditors, auditStartTime: `${time}:00` })
      navigate(fillPath(created))
    } catch {
      /* error shown below */
    }
  }

  const renderField = (f: HeaderField) => {
    const label = <>{f.label}{f.required ? ' *' : ''}</>
    if (f.type === 'DateList') {
      const dates = list(f.key)
      return (
        <div key={f.key} className="date-list">
          <span className="small">{label}</span>
          {dates.map((d, i) => (
            <div key={i} className="row">
              <DateInput value={d} onChange={(v) => setList(f.key, dates.map((x, j) => (j === i ? v : x)))} aria-label={`${f.label} ${i + 1}`} />
              <button type="button" className="ghost" onClick={() => setList(f.key, dates.filter((_, j) => j !== i))} aria-label={t('reports.removeDate')} title={t('reports.removeDate')}>✕</button>
            </div>
          ))}
          <div><button type="button" className="secondary small-btn" onClick={() => setList(f.key, [...dates, ''])}>{t('reports.addDate')}</button></div>
        </div>
      )
    }
    if (f.type === 'DateRange') {
      const [start = '', end = ''] = list(f.key)
      return (
        <div key={f.key} className="date-list">
          <span className="small">{label}</span>
          <div className="grid two">
            <label>{t('reports.rangeStart')}<DateInput value={start} max={end || undefined} onChange={(v) => setList(f.key, [v, end])} /></label>
            <label>{t('reports.rangeEnd')}<DateInput value={end} min={start || undefined} onChange={(v) => setList(f.key, [start, v])} /></label>
          </div>
        </div>
      )
    }
    return (
      <label key={f.key}>{label}
        {f.type === 'MultilineText' ? (
          <textarea value={str(f.key)} onChange={(e) => setStr(f.key, e.target.value)} rows={3} placeholder={f.placeholder ?? ''} />
        ) : f.type === 'Date' ? (
          <DateInput value={str(f.key)} onChange={(v) => setStr(f.key, v)} required={f.required} />
        ) : (
          <input value={str(f.key)} onChange={(e) => setStr(f.key, e.target.value)} placeholder={f.placeholder ?? ''} type={f.type === 'Number' ? 'number' : 'text'} />
        )}
      </label>
    )
  }

  return (
    <Modal title={t('reports.startModalTitle')} onClose={create.isPending ? () => undefined : onClose} width={560}>
      <div className="stack">
        <div><b>{template.name}</b> <span className="muted small">({template.code})</span></div>
        <p className="muted small" style={{ margin: 0 }}>{t('reports.startModalHint')}</p>
        <label>{t('reports.title')}<input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={defaultTitle} autoFocus /></label>
        <div className="grid two">
          <label>{dateField?.label ?? t('reports.startDate')}<DateInput value={date} onChange={setDate} required /></label>
          <label>{t('reports.startTime')}<input type="time" value={time} onChange={(e) => setTime(e.target.value)} required /></label>
        </div>
        <label>{t('companies.title')}
          {companies.length <= 1 ? (
            <input value={company?.name ?? '—'} readOnly />
          ) : (
            <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setSiteId('') }}>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name} ({t(`companyType.${c.type}`)})</option>)}
            </select>
          )}
        </label>
        {company && company.sites.length > 0 ? (
          <label>{t('companies.site')}
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">{t('common.none')}</option>
              {company.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        ) : null}
        <label>{t('reports.auditor')}<input value={user?.fullName ?? ''} readOnly /></label>
        {version.isLoading ? <p className="muted small" style={{ margin: 0 }}>{t('common.loading')}</p> : null}
        {requiredFields.length > 0 ? (
          <div className="stack" style={{ gap: 8 }}>
            <span className="small"><b>{t('reports.requiredHeader')}</b></span>
            {requiredFields.map(renderField)}
          </div>
        ) : null}
        {optionalFields.length > 0 ? (
          <details open={more} onToggle={(e) => setMore((e.target as HTMLDetailsElement).open)}>
            <summary className="small muted">{t('reports.moreHeader')}</summary>
            <div className="stack" style={{ gap: 8, marginTop: 8 }}>{optionalFields.map(renderField)}</div>
          </details>
        ) : null}
        {create.isError ? <p className="error small" style={{ margin: 0 }}>{problemOf(create.error).title ?? t('common.error')}</p> : null}
        {!company ? <p className="error small" style={{ margin: 0 }}>{t('reports.noCompany')}</p> : null}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="secondary" onClick={onClose} disabled={create.isPending}>{t('common.cancel')}</button>
          <button onClick={() => void start()} disabled={!company || !version.data || !date || !timeOk || missingRequired.length > 0 || create.isPending}>{create.isPending ? t('common.pleaseWait') : t('reports.startNow')}</button>
        </div>
      </div>
    </Modal>
  )
}
